/**
 * ANEMOIA SCREENSAVER — the idle watcher (custom).
 *
 * Stand still for a few seconds and the gallery turns into a maze: a chunky,
 * low-resolution first-person corridor that wanders itself, with our products
 * hung on the walls as framed pieces. It is the 1995 3D Maze screensaver read
 * through the brand — the gallery you cannot quite leave, nostalgia for a place
 * that never existed. Any input wakes the page back up.
 *
 * THIS FILE IS THE ONLY THING THAT SHIPS ON PAGE LOAD, and it is deliberately
 * small: some data attributes, a handful of passive listeners and a one-second
 * timer. The renderer (`anemoia-maze.js`), the stylesheet and every product
 * image are fetched the FIRST TIME a visitor actually goes idle, and never at
 * all for a visitor who does not. Nothing here touches the network, the DOM or
 * layout until then. Keep it that way — the whole point of the feature is that
 * it is free until it is wanted.
 *
 * Everything is editor-configurable under Theme settings > Anemoia screensaver
 * (see snippets/anemoia-screensaver.liquid). Setup notes and the tuning knobs
 * live in docs/anemoia-screensaver/README.md.
 */

/** Resolved off this module's own URL, so the snippet only has to link one file. */
const CSS_URL = new URL('./anemoia-screensaver.css', import.meta.url).href;

/** How often we ask "still idle?" — a second is plenty and costs nothing. */
const TICK_MS = 1000;

/**
 * Input events are only used to stamp a timestamp, so we do not need every
 * one: a pointermove flood is throttled down to this.
 */
const STAMP_THROTTLE_MS = 200;

/** Anything that proves a person is there. Used to stamp, and to wake. */
const WAKE_EVENTS = [
  'pointerdown',
  'pointermove',
  'pointerup',
  'keydown',
  'wheel',
  'touchstart',
  'touchmove',
  'scroll',
  'focusin',
  'click',
];

/**
 * The same list minus keydown, which is handled on its own because it is the
 * one input the page can still act on from behind the overlay: pointer and
 * touch events land on the overlay itself, but a keypress goes to the document
 * and would, say, trigger the About page's "press any key to enter" dive on the
 * very keystroke that was only meant to wake the screen.
 */
const SLEEP_EVENTS = WAKE_EVENTS.filter((type) => type !== 'keydown');

/** Something else already owns the screen: a dialog, a popup, the cart drawer. */
const BUSY_SELECTOR = [
  'dialog[open]',
  'anemoia-slot:not([hidden])',
  'anemoia-gameboy:not([hidden])',
].join(',');

const FIELD_SELECTOR = 'input,textarea,select,[contenteditable=""],[contenteditable="true"]';

class AnemoiaScreensaver extends HTMLElement {
  connectedCallback() {
    // The theme editor re-renders constantly and an editor is, by definition,
    // someone staring at a page without touching it. Never here.
    if (this.dataset.designMode === 'true') return;

    // A screensaver is motion and nothing else, so there is no reduced-motion
    // variant to fall back to — there is simply no screensaver.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.delayMs = Math.max(5, Number(this.dataset.delay) || 30) * 1000;

    // Which screens it is allowed on. Kept as a live media query rather than a
    // one-off measurement, so a window dragged across the breakpoint (or a
    // phone turned sideways) is respected without a reload.
    this.scope = this.dataset.devices || 'all';
    this.wide = this.scope === 'all' ? null : window.matchMedia('(min-width: 750px)');

    this.lastInput = performance.now();
    this.stampedAt = 0;
    this.active = false;
    /** Bumped on every wake/sleep so a slow first load cannot land after a wake. */
    this.token = 0;

    // Arm only once the page has finished its real work. `load` plus an idle
    // callback keeps us out of the way of everything that matters.
    const arm = () => this.#arm();
    if (document.readyState === 'complete') this.#whenIdle(arm);
    else window.addEventListener('load', () => this.#whenIdle(arm), { once: true });
  }

  disconnectedCallback() {
    this.#stopTimer();
    for (const type of WAKE_EVENTS) window.removeEventListener(type, this.#stamp, true);
    document.removeEventListener('visibilitychange', this.#onVisibility);
  }

  #whenIdle(fn) {
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(fn, { timeout: 2000 });
    else setTimeout(fn, 500);
  }

  #arm() {
    for (const type of WAKE_EVENTS) {
      window.addEventListener(type, this.#stamp, { passive: true, capture: true });
    }
    document.addEventListener('visibilitychange', this.#onVisibility);
    this.lastInput = performance.now();
    this.#startTimer();
  }

  #startTimer() {
    if (this.timer) return;
    this.timer = window.setInterval(this.#tick, TICK_MS);
  }

  #stopTimer() {
    window.clearInterval(this.timer);
    this.timer = 0;
  }

  /**
   * Every input event lands here. It must stay this cheap — a timestamp and a
   * comparison — because pointermove alone can fire hundreds of times a second.
   */
  #stamp = () => {
    const now = performance.now();
    if (now - this.stampedAt < STAMP_THROTTLE_MS) return;
    this.stampedAt = now;
    this.lastInput = now;
  };

  #onVisibility = () => {
    // Coming back to a tab is not idling in it, and time spent on another tab
    // should not count toward the countdown either.
    this.lastInput = performance.now();
  };

  #tick = () => {
    if (this.active) return;
    if (this.#busy()) {
      this.lastInput = performance.now();
      return;
    }
    if (performance.now() - this.lastInput < this.delayMs) return;
    this.#wake();
  };

  /** True when the screen is already spoken for, or nobody is watching it. */
  #busy() {
    if (document.hidden) return true;
    if (this.wide && (this.scope === 'desktop') !== this.wide.matches) return true;
    if (document.fullscreenElement) return true;
    // Horizon's dialogs (cart drawer, quick add) and our two popups, which both
    // lock the body rather than setting an attribute.
    if (document.documentElement.hasAttribute('scroll-lock')) return true;
    if (document.body.style.overflow === 'hidden') return true;
    if (document.querySelector(BUSY_SELECTOR)) return true;
    // Someone typing slowly (a contact form, a discount code) is not idle.
    const focused = document.activeElement;
    if (focused && focused !== document.body && focused.matches?.(FIELD_SELECTOR)) return true;
    return false;
  }

  async #wake() {
    if (this.active) return;
    this.active = true;
    this.#stopTimer();
    const token = ++this.token;

    try {
      await this.#load();
    } catch (error) {
      // A failed import must not leave a half-dead overlay behind; give up on
      // the feature for this page rather than retrying into the same error.
      this.active = false;
      return;
    }

    // Input arrived while we were loading, or the screen got busy — stand down.
    if (token !== this.token || this.#busy() || performance.now() - this.lastInput < this.delayMs) {
      this.active = false;
      this.#startTimer();
      return;
    }

    await this.maze.prepare();
    if (token !== this.token) {
      this.active = false;
      this.#startTimer();
      return;
    }

    this.overlay.hidden = false;
    this.overlay.classList.remove('is-off');
    // One frame with the element laid out before the class lands, or the
    // transition has nothing to animate from.
    requestAnimationFrame(() => {
      if (token !== this.token) return;
      this.overlay.classList.add('is-on');
    });
    this.maze.play();

    for (const type of SLEEP_EVENTS) window.addEventListener(type, this.#sleep, { passive: true, capture: true });
    // Capture, non-passive and first in line, so the waking keystroke is spent
    // on waking and nothing else.
    window.addEventListener('keydown', this.#swallowKey, { capture: true });
    // Non-passive too: a wheel or a drag over a fixed overlay still scrolls the
    // page behind it.
    this.overlay.addEventListener('wheel', this.#swallow, { passive: false });
    this.overlay.addEventListener('touchmove', this.#swallow, { passive: false });
  }

  #swallow = (event) => {
    event.preventDefault();
  };

  #swallowKey = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#sleep();
  };

  #sleep = () => {
    if (!this.active) return;
    this.active = false;
    this.token++;

    for (const type of SLEEP_EVENTS) window.removeEventListener(type, this.#sleep, true);
    window.removeEventListener('keydown', this.#swallowKey, true);
    this.overlay.removeEventListener('wheel', this.#swallow);
    this.overlay.removeEventListener('touchmove', this.#swallow);

    this.maze.pause();
    this.overlay.classList.remove('is-on');
    this.overlay.classList.add('is-off');
    window.clearTimeout(this.hideTimer);
    this.hideTimer = window.setTimeout(() => {
      this.overlay.hidden = true;
      this.maze.reset();
    }, 320);

    this.lastInput = performance.now();
    this.#startTimer();
  };

  /**
   * First idle only: pull in the stylesheet, the renderer and the overlay DOM.
   * Everything after this is already in memory.
   */
  async #load() {
    if (this.loaded) return this.loaded;
    this.loaded = (async () => {
      const [, module] = await Promise.all([this.#loadCss(), import('./anemoia-maze.js')]);

      const overlay = document.createElement('div');
      overlay.className = 'ascr';
      overlay.hidden = true;
      // Purely decorative, and it must never be reachable by tab or read out.
      overlay.setAttribute('aria-hidden', 'true');
      overlay.innerHTML =
        '<canvas class="ascr__canvas"></canvas><div class="ascr__grain"></div>' +
        '<p class="ascr__caption"></p><p class="ascr__hint"></p>';
      overlay.querySelector('.ascr__hint').textContent = this.dataset.hint || '';
      document.body.appendChild(overlay);
      this.overlay = overlay;

      this.maze = new module.AnemoiaMaze(overlay.querySelector('.ascr__canvas'), {
        art: this.#readArt(),
        // No element means no caption: the renderer already treats a missing
        // one as "do not label", so the setting needs no second code path.
        caption: this.dataset.captions === 'false' ? null : overlay.querySelector('.ascr__caption'),
        wordmark: this.dataset.wordmark || 'ANEMOIA',
        detail: this.dataset.detail,
        speed: Number(this.dataset.speed) / 100,
      });
    })();
    return this.loaded;
  }

  #loadCss() {
    return new Promise((resolve) => {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = CSS_URL;
      // Resolve either way: a missing stylesheet should not block the maze, it
      // should just look wrong, which is loud enough to get noticed.
      link.addEventListener('load', resolve, { once: true });
      link.addEventListener('error', resolve, { once: true });
      document.head.appendChild(link);
    });
  }

  /** The product titles and image URLs the snippet rendered as JSON. */
  #readArt() {
    const node = this.querySelector('[data-ascr-art]');
    if (!node) return [];
    try {
      const parsed = JSON.parse(node.textContent);
      return Array.isArray(parsed) ? parsed.filter((item) => item && item.i) : [];
    } catch (error) {
      return [];
    }
  }
}

if (!customElements.get('anemoia-screensaver')) {
  customElements.define('anemoia-screensaver', AnemoiaScreensaver);
}
